import { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import { Card, Button, Badge, SectionHeader, Table, Td } from '../components/ui';
import toast from 'react-hot-toast';
import {
  ShieldCheck, AlertTriangle, CheckCircle2, Clock, XCircle,
  Camera, Image as ImageIcon, RotateCcw, X, ChevronRight, ChevronLeft,
  User as UserIcon, CreditCard, BookOpen, Info,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Utilities
// ─────────────────────────────────────────────────────────────────────────────

// Must match middleware/upload.js's multer fileFilter — checked here too so
// an unsupported file (most commonly HEIC/HEIF from an iPhone) gets a clear,
// actionable message immediately instead of a round trip to the server.
const ALLOWED_UPLOAD_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];

// 2026-07-24 KYC bug: neither the FileReader nor the Image had an error
// handler. Most browsers can't decode HEIC/HEIF (the default photo format on
// iPhone) into an <img> — img.onload silently never fires, so this promise
// never resolved OR rejected. Since handleFile awaits this before ever
// setting `uploading`, the customer saw the capture button do nothing at
// all, forever, with no error and no network request. Every failure path
// now falls back to resolving with the original file — compression is a
// nice-to-have, not something the flow can be blocked on. The backend's
// multer fileFilter (middleware/upload.js) still rejects real HEIC uploads
// with a proper 400 + clear message, since we can't produce a JPEG from
// a format we can't decode — but the request now actually gets sent, so
// that message reaches the user instead of the flow just hanging.
async function compressImage(file, maxPx = 1600, quality = 0.82) {
  if (!file.type.startsWith('image/')) return file; // PDFs pass through
  return new Promise((resolve) => {
    const fallback = () => resolve(file);
    const reader = new FileReader();
    reader.onerror = fallback;
    reader.onload = (ev) => {
      const img = new window.Image();
      img.onerror = fallback;
      img.onload = () => {
        let w = img.naturalWidth;
        let h = img.naturalHeight;
        if (w > maxPx || h > maxPx) {
          if (w > h) { h = Math.round(h * maxPx / w); w = maxPx; }
          else       { w = Math.round(w * maxPx / h); h = maxPx; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => blob ? resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' })) : fallback(),
          'image/jpeg', quality
        );
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  });
}

async function uploadFile(file, token, onProgress) {
  const form = new FormData();
  form.append('file', file);
  const res = await axios.post('/api/kyc/upload', form, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => onProgress(Math.round((e.loaded * 100) / (e.total || 1))),
  });
  return res.data?.url || '';
}

// ─────────────────────────────────────────────────────────────────────────────
// StepBar
// ─────────────────────────────────────────────────────────────────────────────

const KYC_STEPS = ['Document', 'Front', 'Back', 'Selfie', 'Review'];

function StepBar({ step, hasBack }) {
  const visible = hasBack
    ? KYC_STEPS
    : KYC_STEPS.filter((s) => s !== 'Back');
  const cur = visible.indexOf(KYC_STEPS[step]);
  return (
    <div className="flex items-center gap-0 mb-1">
      {visible.map((label, i) => (
        <div key={label} className="flex items-center flex-1 min-w-0">
          <div className="flex flex-col items-center flex-1 min-w-0">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${
              i < cur  ? 'bg-cyan border-cyan text-[#0a0f1e]'
            : i === cur ? 'border-cyan text-cyan bg-transparent'
            : 'border-border text-text-muted bg-transparent'
            }`}>
              {i < cur ? '✓' : i + 1}
            </div>
            <p className={`text-xs mt-1 text-center leading-tight truncate w-full px-1 ${i === cur ? 'text-text-primary font-semibold' : 'text-text-muted'}`}>
              {label}
            </p>
          </div>
          {i < visible.length - 1 && (
            <div className={`h-0.5 flex-1 mb-4 mx-0.5 transition-colors ${i < cur ? 'bg-cyan' : 'bg-border'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PhotoGuide
// ─────────────────────────────────────────────────────────────────────────────

function PhotoGuide({ title, items, icon: Icon = Info }) {
  return (
    <div className="flex gap-3 p-3 rounded-xl bg-bg-panel border border-border mb-4">
      <Icon className="w-5 h-5 text-cyan flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-text-primary text-xs font-semibold mb-1.5">{title}</p>
        <ul className="space-y-1">
          {items.map((item, i) => (
            <li key={i} className="text-text-secondary text-xs flex gap-1.5">
              <span className="text-cyan flex-shrink-0">&#x2022;</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// UploadProgress
// ─────────────────────────────────────────────────────────────────────────────

function UploadProgress({ progress }) {
  return (
    <div className="mt-3">
      <div className="flex justify-between text-xs text-text-muted mb-1">
        <span>Uploading&hellip;</span>
        <span>{progress}%</span>
      </div>
      <div className="h-1.5 bg-bg-panel rounded-full overflow-hidden border border-border">
        <div
          className="h-full bg-cyan rounded-full transition-all duration-200"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DocumentCapture
// ─────────────────────────────────────────────────────────────────────────────

function DocumentCapture({ label, required, captureMode, guide, onReady, onReset, captured }) {
  const cameraRef  = useRef(null);
  const galleryRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress]   = useState(0);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    const MAX = 8 * 1024 * 1024;
    if (file.size > MAX) { toast.error('File too large. Maximum 8 MB.'); return; }
    if (!ALLOWED_UPLOAD_TYPES.includes(file.type)) {
      toast.error(
        file.type === 'image/heic' || file.type === 'image/heif' || /\.hei[cf]$/i.test(file.name || '')
          ? 'iPhone photo format (HEIC) is not supported. In Settings → Camera → Formats, switch to "Most Compatible", then retake the photo — or choose an existing JPG/PNG.'
          : `Unsupported file type${file.type ? ' (' + file.type + ')' : ''}. Please use JPG, PNG, WebP, or PDF.`
      );
      return;
    }

    let preview = null;
    if (file.type.startsWith('image/')) {
      preview = URL.createObjectURL(file);
    }

    setUploading(true);
    setProgress(0);
    try {
      const compressed = await compressImage(file);
      const token = localStorage.getItem('dwi_token');
      const url   = await uploadFile(compressed, token, setProgress);
      onReady({ url, preview, name: file.name, isPdf: file.type === 'application/pdf' });
      toast.success('Photo saved!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed. Try again.');
      if (preview) URL.revokeObjectURL(preview);
    } finally {
      setUploading(false);
      if (cameraRef.current)  cameraRef.current.value  = '';
      if (galleryRef.current) galleryRef.current.value = '';
    }
  }, [onReady]);

  const handleReset = () => {
    if (captured && captured.preview) URL.revokeObjectURL(captured.preview);
    onReset();
    if (cameraRef.current)  cameraRef.current.value  = '';
    if (galleryRef.current) galleryRef.current.value = '';
  };

  if (captured) {
    return (
      <div>
        <p className="text-text-secondary text-sm font-medium mb-2">
          {label}{required && <span className="text-red ml-1">*</span>}
        </p>
        <div className="rounded-2xl border border-cyan/40 bg-cyan/5 overflow-hidden">
          {captured.isPdf ? (
            <div className="flex items-center gap-3 p-4">
              <div className="w-12 h-12 rounded-xl bg-bg-panel border border-border flex items-center justify-center flex-shrink-0">
                <ImageIcon className="w-6 h-6 text-cyan" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-text-primary text-sm font-semibold truncate">{captured.name}</p>
                <p className="text-cyan text-xs">PDF uploaded &#x2713;</p>
              </div>
            </div>
          ) : (
            <img src={captured.preview} alt={label} className="w-full object-cover max-h-52" style={{ display: 'block' }} />
          )}
          {uploading ? (
            <div className="p-3"><UploadProgress progress={progress} /></div>
          ) : (
            <div className="flex gap-2 p-3 border-t border-cyan/20">
              <button
                onClick={() => cameraRef.current && cameraRef.current.click()}
                className="flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-semibold text-text-secondary border border-border hover:border-border-hi hover:text-text-primary transition-colors"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Retake
              </button>
              <button
                onClick={handleReset}
                className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold text-red/80 border border-red/20 hover:border-red/40 transition-colors"
              >
                <X className="w-3.5 h-3.5" /> Remove
              </button>
            </div>
          )}
        </div>
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture={captureMode}
          className="hidden"
          onChange={(e) => { handleReset(); handleFile(e.target.files && e.target.files[0]); }}
        />
      </div>
    );
  }

  return (
    <div>
      <p className="text-text-secondary text-sm font-medium mb-2">
        {label}{required && <span className="text-red ml-1">*</span>}
      </p>
      {guide && <PhotoGuide {...guide} />}
      {uploading ? (
        <div className="rounded-2xl border border-border bg-bg-panel p-5">
          <div className="flex flex-col items-center gap-2 mb-3">
            <div className="w-10 h-10 border-2 border-cyan border-t-transparent rounded-full animate-spin" />
            <p className="text-text-muted text-xs">Processing photo&hellip;</p>
          </div>
          <UploadProgress progress={progress} />
        </div>
      ) : (
        <div className="rounded-2xl border-2 border-dashed border-border overflow-hidden">
          <button
            type="button"
            onClick={() => cameraRef.current && cameraRef.current.click()}
            className="w-full flex items-center gap-4 p-5 hover:bg-cyan/5 transition-colors group"
          >
            <div className="w-12 h-12 rounded-2xl bg-cyan/10 border border-cyan/30 flex items-center justify-center flex-shrink-0 group-hover:bg-cyan/20 transition-colors">
              <Camera className="w-6 h-6 text-cyan" />
            </div>
            <div className="text-left">
              <p className="text-text-primary text-sm font-bold">Take Photo</p>
              <p className="text-text-muted text-xs">Opens camera on your device</p>
            </div>
            <ChevronRight className="w-4 h-4 text-text-muted ml-auto" />
          </button>
          <div className="border-t border-border mx-4 relative">
            <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-bg-card px-2 text-text-muted text-xs">or</span>
          </div>
          <button
            type="button"
            onClick={() => galleryRef.current && galleryRef.current.click()}
            className="w-full flex items-center gap-4 p-4 hover:bg-bg-panel/50 transition-colors"
          >
            <div className="w-10 h-10 rounded-xl bg-bg-panel border border-border flex items-center justify-center flex-shrink-0">
              <ImageIcon className="w-5 h-5 text-text-secondary" />
            </div>
            <div className="text-left">
              <p className="text-text-secondary text-sm font-semibold">Choose from Gallery</p>
              <p className="text-text-muted text-xs">JPG, PNG, PDF &middot; Max 8 MB</p>
            </div>
          </button>
        </div>
      )}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture={captureMode}
        className="hidden"
        onChange={(e) => handleFile(e.target.files && e.target.files[0])}
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => handleFile(e.target.files && e.target.files[0])}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared sub-components
// ─────────────────────────────────────────────────────────────────────────────

function NavButtons({ onBack, onNext, canNext, nextLabel }) {
  return (
    <div className="flex gap-3 mt-5">
      <Button variant="ghost" onClick={onBack} className="flex-none px-4">
        <ChevronLeft className="w-4 h-4" /> Back
      </Button>
      <Button onClick={onNext} disabled={!canNext} className="flex-1">
        {nextLabel || 'Continue'} <ChevronRight className="w-4 h-4 ml-1" />
      </Button>
    </div>
  );
}

function ReviewThumb({ label, captured, onRetake }) {
  if (!captured) return null;
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-bg-panel border border-border">
      {captured.isPdf ? (
        <div className="w-14 h-14 rounded-xl bg-bg-card border border-border flex items-center justify-center flex-shrink-0">
          <ImageIcon className="w-6 h-6 text-text-muted" />
        </div>
      ) : (
        <img src={captured.preview} alt={label} className="w-14 h-14 rounded-xl object-cover flex-shrink-0 border border-border" />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-text-primary text-xs font-semibold">{label}</p>
        <p className="text-green text-xs">&#x2713; Uploaded</p>
      </div>
      <button
        onClick={onRetake}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-text-muted text-xs hover:border-border-hi hover:text-text-primary transition-colors flex-shrink-0"
      >
        <RotateCcw className="w-3 h-3" /> Retake
      </button>
    </div>
  );
}

function LimitsTable() {
  return (
    <Card>
      <SectionHeader title="Account Limits" />
      <Table headers={['Feature', 'Without KYC', 'With KYC']}>
        {[
          ['Daily Withdrawal',  '$135',              '$10,000'],
          ['Daily Deposit',     '$500',              '$50,000'],
          ['Investment Funds',  'Low & Medium Risk', 'All Funds incl. High Risk'],
          ['P2P Transfers',     '$100/day',          '$5,000/day'],
          ['Account Recovery',  'Email only',        'Full recovery'],
        ].map(([feature, without, withKyc]) => (
          <tr key={feature}>
            <Td className="font-semibold text-text-primary">{feature}</Td>
            <Td className="text-red">{without}</Td>
            <Td className="text-green font-semibold">{withKyc}</Td>
          </tr>
        ))}
      </Table>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Configs
// ─────────────────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  none:     { icon: AlertTriangle, color: 'text-amber', bg: 'bg-amber/10 border-amber/25', label: 'Not Started',  desc: 'Complete KYC to unlock all features and higher limits.' },
  pending:  { icon: Clock,         color: 'text-blue',  bg: 'bg-blue/10 border-blue/25',   label: 'Under Review', desc: 'Your documents are being reviewed. This takes 1-2 business days.' },
  approved: { icon: CheckCircle2,  color: 'text-green', bg: 'bg-green/10 border-green/25', label: 'Verified',     desc: 'KYC approved. All platform features and limits are unlocked.' },
  rejected: { icon: XCircle,       color: 'text-red',   bg: 'bg-red/10 border-red/25',     label: 'Rejected',     desc: 'Your KYC was rejected. Please re-submit with correct documents.' },
};

const NEEDS_BACK = ['national_id', 'drivers_license'];

const DOC_TYPES = {
  national_id:     { label: 'National ID (NIN)',  icon: CreditCard },
  passport:        { label: 'Passport',            icon: BookOpen   },
  drivers_license: { label: "Driver's License",   icon: CreditCard },
};

// ─────────────────────────────────────────────────────────────────────────────
// Main KYC page
// ─────────────────────────────────────────────────────────────────────────────

export default function KYC() {
  const { user, refreshUser } = useAuth();
  const kyc    = user && user.kyc ? user.kyc : {};
  const status = kyc.status || 'none';
  const cfg    = STATUS_CONFIG[status] || STATUS_CONFIG.none;
  const StatusIcon = cfg.icon;

  const [step,       setStep]       = useState(0);
  const [docType,    setDocType]    = useState('national_id');
  const [front,      setFront]      = useState(null);
  const [back,       setBack]       = useState(null);
  const [selfie,     setSelfie]     = useState(null);
  const [submitting,  setSubmitting]  = useState(false);
  const [showWizard,  setShowWizard]  = useState(status !== 'rejected');

  const hasBack    = NEEDS_BACK.includes(docType);
  const STEP_FRONT  = 1;
  const STEP_BACK   = 2;
  const STEP_SELFIE = hasBack ? 3 : 2;
  const STEP_REVIEW = hasBack ? 4 : 3;

  const nextStep = () => {
    if (!hasBack && step === STEP_FRONT) { setStep(STEP_SELFIE); return; }
    setStep((s) => s + 1);
  };
  const prevStep = () => {
    if (!hasBack && step === STEP_SELFIE) { setStep(STEP_FRONT); return; }
    setStep((s) => Math.max(0, s - 1));
  };

  const handleSubmit = async () => {
    if (!front)  { toast.error('Front document photo is required'); return; }
    if (!selfie) { toast.error('Selfie photo is required');         return; }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('dwi_token');
      const auth  = { headers: { Authorization: 'Bearer ' + token } };

      // Save each file to admin-visible KYC documents (non-fatal)
      const saves = [
        axios.post('/api/kyc/documents', { documentType: docType + '_front', fileUrl: front.url }, auth),
        axios.post('/api/kyc/documents', { documentType: 'selfie', fileUrl: selfie.url }, auth),
      ];
      if (back) saves.push(axios.post('/api/kyc/documents', { documentType: docType + '_back', fileUrl: back.url }, auth));
      await Promise.allSettled(saves);

      // Primary submission — updates User.kyc status
      await axios.post('/api/auth/kyc', {
        documentType:  docType,
        documentFront: front.url,
        documentBack:  back   ? back.url   : null,
        selfie:        selfie ? selfie.url : null,
      }, auth);

      toast.success('KYC submitted! Review takes 1-2 business days.');
      await refreshUser();
    } catch (err) {
      console.error('[KYC submit]', err);
      const msg = (err.response && err.response.data && err.response.data.message)
        || err.message
        || 'Submission failed. Try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Approved ──────────────────────────────────────────────────────────────
  if (status === 'approved') {
    return (
      <div className="space-y-6 animate-slide-up max-w-2xl mx-auto">
        <div>
          <h1 className="text-2xl font-black text-text-primary tracking-tight">KYC Verification</h1>
          <p className="text-text-secondary text-sm mt-1">Identity verification status and account limits.</p>
        </div>
        <div className={'flex items-start gap-3 p-4 rounded-2xl border ' + cfg.bg}>
          <StatusIcon className={'w-5 h-5 flex-shrink-0 mt-0.5 ' + cfg.color} />
          <div className="flex-1">
            <p className={'font-bold text-sm ' + cfg.color}>{cfg.label} &#x2713;</p>
            <p className="text-text-secondary text-sm mt-0.5">{cfg.desc}</p>
            {kyc.reviewedAt && (
              <p className="text-text-muted text-xs mt-1">Approved {new Date(kyc.reviewedAt).toLocaleDateString()}</p>
            )}
          </div>
          <Badge label="Verified" color="green" />
        </div>
        <LimitsTable />
      </div>
    );
  }

  // ── Pending ───────────────────────────────────────────────────────────────
  if (status === 'pending') {
    return (
      <div className="space-y-6 animate-slide-up max-w-2xl mx-auto">
        <div>
          <h1 className="text-2xl font-black text-text-primary tracking-tight">KYC Verification</h1>
          <p className="text-text-secondary text-sm mt-1">Identity verification status.</p>
        </div>
        <div className={'flex items-start gap-3 p-4 rounded-2xl border ' + cfg.bg}>
          <StatusIcon className={'w-5 h-5 flex-shrink-0 mt-0.5 ' + cfg.color} />
          <div className="flex-1">
            <p className={'font-bold text-sm ' + cfg.color}>{cfg.label}</p>
            <p className="text-text-secondary text-sm mt-0.5">{cfg.desc}</p>
            {kyc.submittedAt && (
              <p className="text-text-muted text-xs mt-1">Submitted {new Date(kyc.submittedAt).toLocaleDateString()}</p>
            )}
          </div>
          <Badge label="Pending" color="blue" />
        </div>
        <Card>
          <div className="flex flex-col items-center py-8 text-center">
            <Clock className="w-16 h-16 text-blue mb-4" />
            <h3 className="text-text-primary font-bold text-lg mb-2">Documents Under Review</h3>
            <p className="text-text-muted text-sm max-w-xs">
              We are reviewing your {kyc.documentType ? kyc.documentType.replace(/_/g, ' ') : 'documents'} submitted on{' '}
              {kyc.submittedAt ? new Date(kyc.submittedAt).toLocaleDateString() : 'record'}.
              You will be notified within 1-2 business days.
            </p>
          </div>
        </Card>
        <LimitsTable />
      </div>
    );
  }

  // ── Rejected — show card with reason + explicit resubmit button ─────────────
  if (status === 'rejected' && !showWizard) {
    return (
      <div className="space-y-4 animate-slide-up max-w-2xl mx-auto">
        <div>
          <h1 className="text-2xl font-black text-text-primary tracking-tight">KYC Verification</h1>
          <p className="text-text-secondary text-sm mt-1">Identity verification unlocks full platform access and higher limits.</p>
        </div>
        <Card>
          <div className="flex items-start gap-3 mb-4">
            <XCircle className="w-6 h-6 flex-shrink-0 mt-0.5 text-red" />
            <div>
              <p className="font-bold text-text-primary text-sm">Verification not approved</p>
              <p className="text-text-secondary text-xs mt-1">Your previous submission was reviewed and could not be accepted.</p>
            </div>
          </div>
          {kyc.rejectionReason && (
            <div className="bg-red/10 border border-red/25 rounded-xl p-3 mb-4">
              <p className="text-red text-xs font-semibold mb-0.5">Reason from reviewer:</p>
              <p className="text-text-primary text-sm">{kyc.rejectionReason}</p>
            </div>
          )}
          <div className="bg-bg-panel border border-border rounded-xl p-3 mb-4 text-xs text-text-secondary space-y-1">
            <p className="font-semibold text-text-primary">To get approved, make sure:</p>
            <ul className="list-disc list-inside space-y-0.5 text-text-muted">
              <li>Photos are clear, well-lit, and not blurry</li>
              <li>All four corners of your ID are visible</li>
              <li>Your selfie clearly shows your face alongside the ID</li>
              <li>The document is not expired or tampered</li>
            </ul>
          </div>
          <Button className="w-full" size="lg" onClick={() => setShowWizard(true)}>
            <ShieldCheck className="w-4 h-4" />
            Resubmit KYC Documents
          </Button>
        </Card>
        <LimitsTable />
      </div>
    );
  }

  // ── Wizard (none | rejected + showWizard) ─────────────────────────────────
  return (
    <div className="space-y-4 animate-slide-up max-w-2xl mx-auto">
      <div>
        <h1 className="text-2xl font-black text-text-primary tracking-tight">KYC Verification</h1>
        <p className="text-text-secondary text-sm mt-1">Identity verification unlocks full platform access and higher limits.</p>
      </div>

      <Card className="py-3">
        <StepBar step={step} hasBack={hasBack} />
      </Card>

      {status === 'rejected' && (
        <div className={'flex items-start gap-3 p-4 rounded-2xl border ' + cfg.bg}>
          <XCircle className={'w-5 h-5 flex-shrink-0 mt-0.5 ' + cfg.color} />
          <div>
            <p className={'font-bold text-sm ' + cfg.color}>Resubmission — address the rejection reason</p>
            {kyc.rejectionReason && <p className="text-red text-xs mt-1">Reason: {kyc.rejectionReason}</p>}
            <p className="text-text-secondary text-xs mt-1">Submit clearer, corrected photos below.</p>
          </div>
        </div>
      )}

      {/* Step 0 — Document type */}
      {step === 0 && (
        <Card>
          <SectionHeader title="Select Document Type" />
          <p className="text-text-secondary text-xs mb-4">Choose the identity document you will submit. Make sure it is current and not expired.</p>
          <div className="space-y-3 mb-6">
            {Object.entries(DOC_TYPES).map(function(entry) {
              var value = entry[0];
              var info  = entry[1];
              var Icon  = info.icon;
              var selected = docType === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => { setDocType(value); setBack(null); }}
                  className={'w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left ' + (selected ? 'border-cyan bg-cyan/5' : 'border-border hover:border-border-hi')}
                >
                  <div className={'w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ' + (selected ? 'bg-cyan/15' : 'bg-bg-panel')}>
                    <Icon className={'w-5 h-5 ' + (selected ? 'text-cyan' : 'text-text-muted')} />
                  </div>
                  <div className="flex-1">
                    <p className={'text-sm font-bold ' + (selected ? 'text-cyan' : 'text-text-primary')}>{info.label}</p>
                    <p className="text-text-muted text-xs">
                      {NEEDS_BACK.includes(value) ? 'Front + Back + Selfie required' : 'Front page + Selfie required'}
                    </p>
                  </div>
                  {selected && <CheckCircle2 className="w-5 h-5 text-cyan flex-shrink-0" />}
                </button>
              );
            })}
          </div>
          <div className="bg-amber/10 border border-amber/25 rounded-xl p-3 text-amber text-xs mb-5">
            <span className="font-semibold">Your privacy:</span> All documents are encrypted and reviewed only by authorised staff. Never shared with third parties.
          </div>
          <Button onClick={nextStep} className="w-full" size="lg">
            Continue <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </Card>
      )}

      {/* Step 1 — Front */}
      {step === STEP_FRONT && (
        <Card>
          <SectionHeader title={DOC_TYPES[docType].label + ' — Front'} />
          <DocumentCapture
            label="Front of document"
            required
            captureMode="environment"
            guide={{
              title: 'Tips for a clear photo',
              icon: Info,
              items: [
                'Place the document on a flat, dark surface',
                'Ensure all four corners are visible in the frame',
                'All text must be sharp and readable',
                'Avoid shadows, glare, and flash reflections',
                'Use natural light or a bright indoor light source',
              ],
            }}
            captured={front}
            onReady={(c) => setFront(c)}
            onReset={() => setFront(null)}
          />
          <NavButtons
            onBack={prevStep}
            onNext={nextStep}
            canNext={!!front}
            nextLabel={hasBack ? 'Next: Back of Document' : 'Next: Selfie'}
          />
        </Card>
      )}

      {/* Step 2 — Back (conditional) */}
      {hasBack && step === STEP_BACK && (
        <Card>
          <SectionHeader title={DOC_TYPES[docType].label + ' — Back'} />
          <DocumentCapture
            label="Back of document"
            required
            captureMode="environment"
            guide={{
              title: 'Flip the document over',
              icon: Info,
              items: [
                'Turn the document to show the back side',
                'Keep the document flat and well-lit',
                'Make sure the barcode or magnetic strip is visible',
                'All text and numbers must be clearly readable',
              ],
            }}
            captured={back}
            onReady={(c) => setBack(c)}
            onReset={() => setBack(null)}
          />
          <NavButtons
            onBack={prevStep}
            onNext={nextStep}
            canNext={!!back}
            nextLabel="Next: Selfie"
          />
        </Card>
      )}

      {/* Selfie step */}
      {step === STEP_SELFIE && (
        <Card>
          <SectionHeader title="Selfie with Document" />
          <DocumentCapture
            label="Hold document next to your face"
            required
            captureMode="user"
            guide={{
              title: 'How to take your selfie',
              icon: UserIcon,
              items: [
                'Hold your document beside your face so both are visible',
                'Look directly at the camera with a neutral expression',
                'Remove hats, sunglasses, or face coverings',
                'Use good lighting and avoid standing against a bright background',
                'Your entire face and the full document must be in frame',
              ],
            }}
            captured={selfie}
            onReady={(c) => setSelfie(c)}
            onReset={() => setSelfie(null)}
          />
          <NavButtons
            onBack={prevStep}
            onNext={nextStep}
            canNext={!!selfie}
            nextLabel="Review and Submit"
          />
        </Card>
      )}

      {/* Review step */}
      {step === STEP_REVIEW && (
        <Card>
          <SectionHeader title="Review Your Submission" />
          <p className="text-text-secondary text-xs mb-4">Check that all photos are clear before submitting. You cannot change them once submitted.</p>
          <div className="space-y-4 mb-5">
            <ReviewThumb label="Front of document" captured={front} onRetake={() => { setFront(null); setStep(STEP_FRONT); }} />
            {back && <ReviewThumb label="Back of document" captured={back} onRetake={() => { setBack(null); setStep(STEP_BACK); }} />}
            <ReviewThumb label="Selfie with document" captured={selfie} onRetake={() => { setSelfie(null); setStep(STEP_SELFIE); }} />
          </div>
          <div className="bg-bg-panel border border-border rounded-xl p-3 text-xs text-text-muted mb-5">
            <p className="font-semibold text-text-secondary mb-1">Document: {DOC_TYPES[docType] && DOC_TYPES[docType].label}</p>
            <p>By submitting, you confirm these are genuine documents belonging to you. False submissions may result in permanent account suspension.</p>
          </div>
          <div className="flex gap-3">
            <Button variant="ghost" onClick={prevStep} className="flex-none px-4">
              <ChevronLeft className="w-4 h-4" /> Back
            </Button>
            <Button
              className="flex-1"
              size="lg"
              loading={submitting}
              disabled={!front || !selfie}
              onClick={handleSubmit}
            >
              <ShieldCheck className="w-4 h-4" />
              Submit for Review
            </Button>
          </div>
        </Card>
      )}

      {/* Benefits card — only on step 0 */}
      {step === 0 && (
        <Card>
          <SectionHeader title="Why Verify?" />
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { icon: '💸', title: 'Higher Limits',        desc: 'Withdraw up to $10,000/day (vs $135 unverified)' },
              { icon: '📈', title: 'All Investment Funds', desc: 'Access high-yield futures and P2P funds' },
              { icon: '🔐', title: 'Account Security',     desc: 'Full account recovery and 2FA support' },
              { icon: '⚡', title: 'Faster Processing',    desc: 'Priority withdrawals and deposits' },
            ].map((b) => (
              <div key={b.title} className="flex gap-3 p-3 rounded-xl bg-bg-panel border border-border">
                <span className="text-xl">{b.icon}</span>
                <div>
                  <p className="text-text-primary text-xs font-semibold">{b.title}</p>
                  <p className="text-text-muted text-xs">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {step === 0 && <LimitsTable />}
    </div>
  );
}
